import { usePageTitle } from '../hooks/usePageTitle';
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { dashboardAPI, seedAPI } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { formatDate, getStatusColor, getPhaseLabel } from '../lib/utils';
import StickyNotesWidget from '../components/StickyNotesWidget';
import {
  Building2,
  Users,
  TrendingUp,
  Calendar,
  CheckSquare,
  MessageSquare,
  ArrowUpRight,
  ArrowDownRight,
  Clock,
  AlertCircle,
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from 'recharts';

const PHASE_COLORS = ['#22c55e', '#3b82f6', '#f59e0b', '#8b5cf6', '#ef4444'];

export const AdminDashboard = () => {
  const { user } = useAuth();
  usePageTitle('Dashboard');
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadDashboard();
  }, []);

  const loadDashboard = async () => {
    try {
      const response = await dashboardAPI.getStats();
      setStats(response.data);
    } catch (error) {
      console.error('Failed to load dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSeed = async () => {
    try {
      const response = await seedAPI.seed();
      toast.success('Demo data loaded successfully!');
      console.log('Seed credentials:', response.data.credentials);
      loadDashboard();
    } catch (error) {
      if (error.response?.data?.message?.includes('already seeded')) {
        toast.info('Database already contains demo data');
      } else {
        toast.error('Failed to seed database');
      }
    }
  };

  const phaseChartData = stats?.phase_distribution
    ? Object.entries(stats.phase_distribution).map(([phase, count]) => ({
        name: getPhaseLabel(phase).split(' ')[0],
        fullName: getPhaseLabel(phase),
        value: count,
      }))
    : [];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-in" data-testid="admin-dashboard">
      {/* Welcome Header */}
      <div className="relative overflow-hidden rounded-xl bg-gradient-to-r from-[#0f4c81] via-[#1a6cb4] to-[#2196F3] p-6 text-white">
        <div className="absolute inset-0 opacity-10" style={{backgroundImage:'radial-gradient(circle at 20% 80%, rgba(255,255,255,0.3) 0%, transparent 50%), radial-gradient(circle at 80% 20%, rgba(255,255,255,0.2) 0%, transparent 50%)'}} />
        <div className="relative z-10 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-heading font-bold">
              Welcome back, {user?.name?.split(' ')[0]}!
            </h1>
            <p className="text-blue-100 mt-1">
              Here's what's happening with your NABH consultancy today.
            </p>
          </div>
        </div>
      </div>

      {/* Sticky Notes (client-wise collaborative notes) — pinned to top */}
      <StickyNotesWidget />

      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        <Card className="border-0 shadow-md bg-gradient-to-br from-blue-50 to-indigo-50 hover:shadow-lg transition-shadow cursor-pointer" data-testid="stat-clients" onClick={() => navigate('/clients')}>
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <div className="p-2.5 bg-blue-100 rounded-xl">
                <Building2 className="w-5 h-5 text-blue-600" />
              </div>
              <Badge className="bg-emerald-100 text-emerald-700">
                <ArrowUpRight className="w-3 h-3 mr-1" />12%
              </Badge>
            </div>
            <div className="mt-4">
              <p className="text-2xl font-bold text-blue-700">{stats?.active_clients || 0}</p>
              <p className="text-sm text-blue-600/70 font-medium">Active Clients</p>
            </div>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-md bg-gradient-to-br from-violet-50 to-purple-50 hover:shadow-lg transition-shadow" data-testid="stat-leads">
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <div className="p-2.5 bg-violet-100 rounded-xl">
                <TrendingUp className="w-5 h-5 text-violet-600" />
              </div>
              <Badge className="bg-emerald-100 text-emerald-700">
                <ArrowUpRight className="w-3 h-3 mr-1" />8%
              </Badge>
            </div>
            <div className="mt-4">
              <p className="text-2xl font-bold text-violet-700">{stats?.total_leads || 0}</p>
              <p className="text-sm text-violet-600/70 font-medium">Total Leads</p>
            </div>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-md bg-gradient-to-br from-amber-50 to-orange-50 hover:shadow-lg transition-shadow cursor-pointer" data-testid="stat-tasks" onClick={() => navigate('/tasks')}>
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <div className="p-2.5 bg-amber-100 rounded-xl">
                <CheckSquare className="w-5 h-5 text-amber-600" />
              </div>
              <Badge className="bg-red-100 text-red-700">
                <Clock className="w-3 h-3 mr-1" />Due
              </Badge>
            </div>
            <div className="mt-4">
              <p className="text-2xl font-bold text-amber-700">{stats?.pending_tasks || 0}</p>
              <p className="text-sm text-amber-600/70 font-medium">Pending Tasks</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Phase Distribution */}
        <Card data-testid="phase-distribution-chart">
          <CardHeader>
            <CardTitle className="text-lg">Clients by Phase</CardTitle>
            <CardDescription>Distribution of active clients across NABH phases</CardDescription>
          </CardHeader>
          <CardContent>
            {phaseChartData.length > 0 ? (
              <div style={{ width: '100%', height: 260 }}>
                <ResponsiveContainer width="100%" height={260}>
                  <PieChart>
                    <Pie
                      data={phaseChartData}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={90}
                      paddingAngle={5}
                      dataKey="value"
                    >
                      {phaseChartData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={PHASE_COLORS[index % PHASE_COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip
                      content={({ active, payload }) => {
                        if (active && payload && payload.length) {
                          return (
                            <div className="bg-white dark:bg-slate-800 p-2 rounded shadow-lg border">
                              <p className="font-medium">{payload[0].payload.fullName}</p>
                              <p className="text-sm text-muted-foreground">{payload[0].value} clients</p>
                            </div>
                          );
                        }
                        return null;
                      }}
                    />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <div className="h-64 flex items-center justify-center text-muted-foreground">
                <p>No client data available</p>
              </div>
            )}
            <div className="flex flex-wrap gap-2 mt-4 justify-center">
              {phaseChartData.map((entry, index) => (
                <div key={`${entry.name}-${index}`} className="flex items-center gap-2">
                  <div
                    className="w-3 h-3 rounded-full"
                    style={{ backgroundColor: PHASE_COLORS[index % PHASE_COLORS.length] }}
                  />
                  <span className="text-xs text-muted-foreground">{entry.name}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Recent Activities */}
        <Card data-testid="recent-activities">
          <CardHeader>
            <CardTitle className="text-lg">Recent Activities</CardTitle>
            <CardDescription>Latest updates across all projects</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {stats?.recent_activities?.length > 0 ? (
                stats.recent_activities.slice(0, 5).map((activity, index) => (
                  <div key={index} className="flex items-start gap-3">
                    <div className="p-2 bg-slate-100 rounded-lg">
                      {activity.type === 'task' ? (
                        <CheckSquare className="w-4 h-4 text-slate-600" />
                      ) : (
                        <Calendar className="w-4 h-4 text-slate-600" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{activity.title}</p>
                      <div className="flex items-center gap-2 mt-1">
                        <Badge variant="secondary" className={getStatusColor(activity.status)}>
                          {activity.status?.replace('_', ' ')}
                        </Badge>
                        <span className="text-xs text-muted-foreground">
                          {formatDate(activity.date)}
                        </span>
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  <AlertCircle className="w-8 h-8 mx-auto mb-2 opacity-50" />
                  <p>No recent activities</p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Button
          variant="outline"
          className="h-auto py-4 flex flex-col items-center gap-2"
          onClick={() => navigate('/leads')}
          data-testid="quick-action-leads"
        >
          <TrendingUp className="w-5 h-5" />
          <span>New Lead</span>
        </Button>
        <Button
          variant="outline"
          className="h-auto py-4 flex flex-col items-center gap-2"
          onClick={() => navigate('/clients')}
          data-testid="quick-action-clients"
        >
          <Building2 className="w-5 h-5" />
          <span>Add Client</span>
        </Button>
        <Button
          variant="outline"
          className="h-auto py-4 flex flex-col items-center gap-2"
          onClick={() => navigate('/visits')}
          data-testid="quick-action-visits"
        >
          <Calendar className="w-5 h-5" />
          <span>Schedule Visit</span>
        </Button>
        <Button
          variant="outline"
          className="h-auto py-4 flex flex-col items-center gap-2"
          onClick={() => navigate('/tickets')}
          data-testid="quick-action-tickets"
        >
          <MessageSquare className="w-5 h-5" />
          <span>View Tickets</span>
        </Button>
      </div>
    </div>
  );
};

export default AdminDashboard;
